import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Input } from '../../../../components/ui/input';
import {
  MagnifyingGlassIcon,
  ArrowTurnDownRightIcon,
  DocumentDuplicateIcon,
  ArrowPathIcon,
  HandRaisedIcon,
  PencilSquareIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import { TeamRegistrationService } from '../../../TeamRegistration/TeamRegistrationService';
import axiosInstance from '../../../../axiosConfig';
import {
  RegisteredTeam,
  TeamRow,
} from '../../../TeamRegistration/TeamRegistration.types';
import {
  ExclamationTriangleIcon,
  CalendarIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { useReportStore } from '../../../../store/reportStore';
import type { ReportData } from '../../../../store/reportStore';
import { useDialogStore } from '../../../../store/dialogStore';
import DialogLayout from '../../../../components/customLayout/dialog';
import { useClubStore } from '../../../../store/clubStore';
import { useGlobalStore } from '../../../../store/globalStore';
import RegisteredClasses from '../../../../components/association/RegisteredClasses';
import PdfLink from '../../../../components/association/PdfLink';
import ResendMailButton from '../../../../components/association/ResendMailButton';
import ReportExcelDownload from './ReportExcelDownload';

const ReportList: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [teamData, setTeamData] = useState<TeamRow[]>([]);
  const setReport = useReportStore((state) => state.setReport);
  const refreshVersion = useReportStore((state) => state.refreshVersion);
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const initialArchive = params.get('archive') === '1';
  const editIdQs = params.get('edit');
  const [archive, setArchive] = useState<boolean>(initialArchive);
  const openDialog = useDialogStore((state) => state.openDialog);
  const closeDialog = useDialogStore((state) => state.closeDialog);
  const setCloseButtonLabel = useDialogStore(
    (state) => state.setCloseButtonLabel,
  );
  const setSaveButtonLabel = useDialogStore(
    (state) => state.setSaveButtonLabel,
  );
  const clubs = useClubStore((state) => state.clubs);
  const setLoading = useGlobalStore((state) => state.setLoading);

  const getClubNameByCountryNumber = useCallback(
    (countryNumber?: string) => {
      if (!countryNumber) return '';
      const numeric = Number(countryNumber);
      const found = clubs.find((c) => Number(c.club_number) === numeric);
      return found?.name ?? countryNumber;
    },
    [clubs],
  );

  const buildReportFromTeam = useCallback((team: TeamRow): ReportData => {
    const toDottedDate = (value?: string) =>
      value ? String(value).split(' ')[0] : '';
    return {
      id: team.id,
      title: team.title,
      invoiceDate: toDottedDate(team.invoice_date),
      datumVon: toDottedDate(team.date_start),
      datumBis: toDottedDate(team.date_end),
      list: team.list ?? [],
    };
  }, []);

  const handleEdit = useCallback(
    (team: TeamRow) => {
      const qs = new URLSearchParams(location.search);
      const current = qs.get('edit');
      if (current === String(team.id)) {
        qs.delete('edit');
      } else {
        qs.set('edit', String(team.id));
        const report = buildReportFromTeam(team);
        setReport(report);
      }
      navigate({ pathname: location.pathname, search: qs.toString() });
    },
    [
      buildReportFromTeam,
      location.pathname,
      location.search,
      navigate,
      setReport,
    ],
  );

  const handleCopy = useCallback(
    (team: TeamRow) => {
      const report = buildReportFromTeam(team);
      setReport(report);
    },
    [buildReportFromTeam, setReport],
  );

  const bumpRefresh = useReportStore((state) => state.bumpRefresh);

  const confirmDelete = (team: TeamRow) => {
    // Optional: ensure labels
    setCloseButtonLabel('Abbruch');
    setSaveButtonLabel('Weiter');

    const content = (
      <div className="grid gap-3">
        <div className="text-base font-bold">
          Sind Sie sicher, dass Sie diesen Eintrag löschen möchten?
        </div>
        <div className="text-xs text-gray-600">
          Dieser Vorgang kann nicht rückgängig gemacht werden.
        </div>
        <div className="border rounded-md p-2 bg-gray-50">
          <div className="font-medium">#{team.id}</div>
          <div className="font-medium">{team.title}</div>
        </div>
      </div>
    );

    const onSave = async () => {
      try {
        setLoading(true);
        await axiosInstance.delete(`association/report/${team.id}`);
        bumpRefresh();
        closeDialog();
      } catch (error) {
        console.error('Delete report error:', error);
      } finally {
        setLoading(false);
      }
    };

    openDialog(content, onSave, false);
  };

  useEffect(() => {
    const fetchAssociationReportList = async () => {
      try {
        setIsLoading(true);
        const data =
          await TeamRegistrationService.fetchAssociationReportList(archive);
        setTeamData(data);
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching report list:', error);
      }
    };
    fetchAssociationReportList();
  }, [archive, refreshVersion]);

  useEffect(() => {
    const qs = new URLSearchParams(location.search);
    const hasArchive = qs.get('archive') === '1';
    if (hasArchive !== archive) {
      setArchive(hasArchive);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const switchTab = (nextArchive: boolean) => {
    setArchive(nextArchive);
    const qs = new URLSearchParams(location.search);
    if (nextArchive) {
      qs.set('archive', '1');
    } else {
      qs.delete('archive');
    }
    navigate({ pathname: location.pathname, search: qs.toString() });
  };

  const filteredData = useMemo(() => {
    return teamData.filter((team) =>
      team.title.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [teamData, searchTerm]);

  return (
    <div className="bg-white border rounded-md p-4 min-w-[440px]">
      <div className="flex flex-col lg:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Nach Titel suchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex items-center gap-2 text-xs font-bold">
          <button
            type="button"
            onClick={() => switchTab(false)}
            className={`relative rounded-full py-2 px-4 ${!archive ? 'bg-[#52A445] text-white' : 'bg-gray-100 text-gray-500'}`}
          >
            Aktiv
            {!archive && (
              <span
                aria-hidden="true"
                className="pointer-events-none absolute left-1/2 -translate-x-1/2 -bottom-1 w-0 h-0 border-x-4 border-x-transparent border-t-4 border-t-[#52A445]"
              ></span>
            )}
          </button>
          <button
            type="button"
            onClick={() => switchTab(true)}
            className={`rounded-full py-2 px-4 ${archive ? 'bg-[#52A445] text-white relative' : 'bg-gray-100 text-gray-500'}`}
          >
            Archiv
            {archive && (
              <span
                aria-hidden="true"
                className="pointer-events-none absolute left-1/2 -translate-x-1/2 -bottom-1 w-0 h-0 border-x-4 border-x-transparent border-t-4 border-t-[#52A445]"
              ></span>
            )}
          </button>
        </div>
      </div>

      {!archive && (
        <div className="flex justify-start -mt-2 mb-4">
          <ReportExcelDownload disabled={isLoading || teamData.length === 0} />
        </div>
      )}

      {isLoading && (
        <div className="text-center py-10 text-gray-500">
          <ArrowPathIcon className="w-10 h-10 mx-auto mb-4 animate-spin" />
        </div>
      )}

      {!isLoading && filteredData.length === 0 && (
        <div className="text-center py-10 text-gray-500">
          <ExclamationTriangleIcon className="w-10 h-10 mx-auto mb-4" />
          <b>Keine Jahresmeldungen gefunden.</b>
          {searchTerm && (
            <div className="text-xs text-gray-500 mt-2">
              Für "
              <span className="font-semibold text-gray-700">{searchTerm}</span>"
              wurden keine Ergebnisse gefunden. <br /> Bitte versuchen Sie es
              mit einem anderen Suchbegriff.
            </div>
          )}
        </div>
      )}

      <div className="relative w-full overflow-auto text-sm">
        {filteredData.map((team, index) => (
          <div
            key={index}
            className={`border-2 mb-2 relative rounded-md ${editIdQs === String(team.id) ? 'border-orange-500 border-2' : ' border-gray-100'}`}
          >
            <div
              className={`absolute w-2 h-4 bg-orange-500 rounded-full top-2 -left-1 ${editIdQs === String(team.id) ? 'block' : 'hidden'}`}
            ></div>
            <div className="flex items-stretch">
              <div className="flex flex-col gap-1 p-2">
                <div
                  className={`text-xs  font-bold text-gray-500 ${editIdQs === String(team.id) ? 'text-orange-500' : 'text-gray-500'}`}
                >
                  #{team.id}
                </div>
                <div
                  className="border-2 border-gray-200 active:bg-gray-100 items-center mt-3 py-1.5 hover:border-gray-500 cursor-pointer px-1.5 rounded-md flex items-center gap-1 justify-center flex-1"
                  onClick={() => handleCopy(team)}
                >
                  <DocumentDuplicateIcon className="w-4 h-4" />
                </div>
                <div
                  className={`border-2 ${editIdQs === String(team.id) ? 'border-orange-500 hover:border-orange-500' : 'border-gray-200'} active:bg-gray-100 items-center mt-1 py-1.5 hover:border-gray-500 cursor-pointer px-1.5 rounded-md flex items-center gap-1 justify-center flex-1`}
                  onClick={() => handleEdit(team)}
                >
                  <PencilSquareIcon
                    className={`w-4 h-4 ${editIdQs === String(team.id) ? 'text-orange-500' : 'text-black'}`}
                  />
                </div>
              </div>
              <div className="flex-1 min-w-0 p-2">
                {editIdQs === String(team.id) &&
                  team.registeredTeams &&
                  team.registeredTeams.length > 0 && (
                    <div className="max-w-[322px] font-medium p-2 bg-yellow-50/40 border border-yellow-500/20 rounded-md my-3 text-xs text-black flex items-start gap-3">
                      <div className="bg-yellow-500/20 rounded-full p-1">
                        <ExclamationCircleIcon className="w-6 h-6" />
                      </div>{' '}
                      Sie befinden sich im Bearbeitungsmodus. Wenn Sie in dieser
                      Zeit Änderungen vornehmen, müssen Sie bedenken, dass{' '}
                      {team.registeredTeams.length} Clubs bereits eine PDF-Datei
                      erhalten haben und von dieser Änderung nichts erfahren
                      werden.
                    </div>
                  )}
                <div className="font-bold mb-1">{team.title}</div>
                <div className="text-xs bg-gray-50 p-2 rounded-md flex justify-between text-gray-500 hover:text-black mb-2">
                  <div className="grid items-center gap-1">
                    <div className="flex items-center gap-1">
                      <CalendarIcon className="w-4 h-4" /> {team.date_start}
                    </div>
                    <div className="flex items-center gap-1">
                      <CalendarIcon className="w-4 h-4" /> {team.date_end}
                    </div>
                  </div>
                  <div className="grid items-center">
                    {' '}
                    <div className="text-[10px]">Rechnungsdatum</div>{' '}
                    {team.invoice_date}
                  </div>
                </div>
                <div className="text-xs text-gray-700">
                  ⏰ Meldeschluss: {team.deadline}
                </div>
                <div className="min-h-[20px] max-w-[310px] flex flex-col gap-1 text-gray-500 border-l border-gray-300 text-xs border-dashed pl-2 ml-1.5 mt-2">
                  {(team.list ?? []).slice(0, 8).map((g, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between gap-2"
                    >
                      <div className="flex flex-1 items-start gap-1">
                        <ArrowTurnDownRightIcon className="w-3 h-3" /> {g.text}
                      </div>
                      <div className="w-[32px] bg-white text-right">{`${g.price}€`}</div>
                      <div
                        className={`${g.vat > 0 ? 'text-gray-500' : 'text-gray-200'} w-[25px] text-right`}
                      >{`${g.vat}%`}</div>
                      <div className="text-right w-[25px]">{`${g.min}-${g.max}`}</div>
                    </div>
                  ))}
                </div>
                <div className="flex mt-2 items-center text-xs justify-between">
                  {team.registeredTeams?.length === 0 ? (
                    <div className="flex items-center mt-5 justify-between w-full">
                      <div className="text-gray-800 flex items-center font-medium gap-1">
                        <HandRaisedIcon className="w-4 h-4" />
                        Noch keine Teams registriert.
                      </div>
                      <div
                        className="text-red-500 flex items-center gap-1 hover:underline cursor-pointer font-medium"
                        onClick={() => confirmDelete(team)}
                      >
                        <TrashIcon className="w-4 h-4" />
                        Eintrag löschen
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between gap-2 w-full">
                      <span
                        className="underline cursor-pointer text-blue-500"
                        onClick={() => {
                          const content = (
                            <div className="-mx-16 text-sm">
                              <div className="font-bold mb-2 pr-14 pb-2 border-b border-gray-100">
                                Registrierte Teams (
                                {team.registeredTeams?.length})
                              </div>
                              <div className="grid gap-2 max-h-[min(60vh,400px)] overflow-y-auto overflow-x-hidden pr-1 pt-1">
                                {(team.registeredTeams ?? []).map(
                                  (rt: RegisteredTeam) => (
                                    <div
                                      key={rt.id}
                                      className="border border-gray-200 hover:border-gray-300 rounded-md p-2 flex flex-wrap items-start justify-between gap-2"
                                    >
                                      <div className="flex flex-col min-w-0 flex-1 basis-[200px]">
                                        <span className="font-medium break-words">
                                          {rt.club_number_country} -{' '}
                                          {getClubNameByCountryNumber(
                                            rt.club_number_country,
                                          )}
                                        </span>
                                        <span className="text-xs text-gray-600 break-words">
                                          von: {rt.melder || '—'} am {rt.date}
                                        </span>
                                        <RegisteredClasses
                                          registration={rt}
                                          list={team.list}
                                        />
                                      </div>
                                      <div className="flex items-center gap-2 shrink-0 ml-auto">
                                        <PdfLink
                                          id={rt.id}
                                          clubNumberCountry={
                                            rt.club_number_country
                                          }
                                        />
                                        <ResendMailButton
                                          id={rt.id}
                                          clubNumberCountry={
                                            rt.club_number_country
                                          }
                                        />
                                      </div>
                                    </div>
                                  ),
                                )}
                              </div>
                            </div>
                          );
                          openDialog(content, () => {}, true);
                        }}
                      >
                        Anzahl der Anmeldungen: {team.registeredTeams?.length}{' '}
                        Teams.
                      </span>
                      <ReportExcelDownload reportId={team.id} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <DialogLayout />
    </div>
  );
};

export default ReportList;
